/**
 * pages/admin-analytics.ts —— 管理后台 · 活跃统计 Tab（2026-09-15）
 * 出处：01_需求文档/14_活跃统计方案.MD §五
 *
 * ## 四块，同出一次请求
 *   ① 三项核心数字  ② 活跃趋势（SVG 两条线）  ③ 功能使用（可展开动作）  ④ 用户使用明细（可展开功能）
 *
 * 🔴 **一个数都不在这里算**（红线 4、前端纪律 2）：去重、访问切分、周界、分桶、「本期有没有记录」
 *   全部由 `GET /api/analytics/overview` 算好下发。
 * ⚠ 用户表的**姓名搜索 / 活跃筛选 / 列排序**在前端做：它们只是把服务端已经算好的行
 *   换个顺序或藏起来，不产生任何新数字；并列时回落到服务端给的默认顺序，所以不会出现两套排序规则。
 *   这几个控件**只作用于用户表**，不改上方全站数字（`14` §5.2），放在用户表自己的标题栏里。
 * ⚠ 查询条件（时间 / 粒度）变了才重新请求；展开、搜索、排序只重画对应那一块，不重新请求。
 */
import type {
  AnalyticsBucket,
  AnalyticsFeatureStat,
  AnalyticsOverview,
  AnalyticsPreset,
  AnalyticsQuery,
  AnalyticsUserRow,
} from '@shared-types';
import { analytics as api } from '../api/index.js';
import { emptyHtml, toast } from '../shared/common.js';
import { $, esc } from '../shared/dom.js';
import { EIC, IC } from '../shared/icons.js';
import { TYPE_LABEL } from '../shared/nav.js';
import { state } from '../shared/state.js';
import { refreshCurrent } from '../router/render.js';

const PRESETS: ReadonlyArray<readonly [AnalyticsPreset, string]> = [
  ['month', '本月'],
  ['lastMonth', '上月'],
  ['custom', '自定义'],
];
const GRAINS: ReadonlyArray<readonly [string, string]> = [
  ['day', '日'],
  ['week', '周'],
  ['biweek', '双周'],
];
const SINCE_TEXT = '2026-09-14 00:00';

/* ===================== 取数 ===================== */

export function queryOf(): AnalyticsQuery {
  const a = state.an;
  const preset = (PRESETS.some(([k]) => k === a.preset) ? a.preset : 'month') as AnalyticsPreset;
  const grain = (GRAINS.some(([k]) => k === a.grain) ? a.grain : 'day') as AnalyticsQuery['grain'];
  return preset === 'custom' ? { preset, grain, from: a.from, to: a.to } : { preset, grain };
}

export function load(signal: AbortSignal): Promise<AnalyticsOverview> {
  return api.overview(queryOf(), { signal });
}

/* ===================== 小工具（只做格式，不做口径） ===================== */

const opt = (v: string, cur: string, lab: string): string =>
  `<option value="${esc(v)}"${v === cur ? ' selected' : ''}>${esc(lab)}</option>`;
/** 'YYYY-MM-DD HH:mm:ss' → 'YYYY-MM-DD HH:mm'。⚠ 字符串截取，不 new Date()（前端纪律） */
const hm = (s: string): string => s.slice(0, 16);
/** 'YYYY-MM-DD' → 'MM-DD' */
const md = (s: string): string => s.slice(5, 10);
const num = (n: number, unit: string): string => `<span class="num">${n}</span><span class="unit">${unit}</span>`;
const dash = (tip: string): string => `<span class="dash" title="${esc(tip)}">—</span>`;

/* ===================== ① 工具条：时间与粒度 ===================== */

export function toolbar(v: AnalyticsOverview): string {
  const a = state.an;
  const custom = a.preset === 'custom';
  const r = v.range;
  return `
  <div class="toolbar an-bar">
    <span class="ck-sel">统计时间
      <select id="an-preset" aria-label="统计时间">${PRESETS.map(([k, l]) => opt(k, a.preset, l)).join('')}</select></span>
    ${
      custom
        ? `<span class="an-dates">
            <input type="date" id="an-from" value="${esc(a.from || r.from)}" aria-label="开始日期">
            <span class="an-to">～</span>
            <input type="date" id="an-to" value="${esc(a.to || r.to)}" aria-label="结束日期（含）">
            <button class="btn primary" data-an="query">查询</button>
          </span>`
        : `<span class="an-span num">${esc(r.from)} ～ ${esc(r.to)}</span>`
    }
    <span class="cnt">范围：全部真实账号，含管理员，不含测试账号</span>
  </div>`;
}

/* ===================== 范围说明 ===================== */

function rangeNote(v: AnalyticsOverview): string {
  const r = v.range;
  const bits: string[] = [];
  if (r.start && r.end) bits.push(`统计 <b class="num">${esc(hm(r.start))}</b> 至 <b class="num">${esc(hm(r.end))}</b>`);
  if (r.clippedBySince) bits.push(`所选时间早于统计起点，${SINCE_TEXT} 之前不计`);
  if (r.ongoing && r.start) bits.push('本周期未结束，数字截至上述时刻');
  return bits.length ? `<p class="an-note">${bits.join('；')}。</p>` : '';
}

/* ===================== ② 核心数字 ===================== */

function cardsHtml(v: AnalyticsOverview): string {
  const c = v.cards;
  if (!c) return '';
  const card = (label: string, n: number, unit: string, tip: string): string => `
    <div class="an-card">
      <div class="an-card-lab">${label}</div>
      <div class="an-card-val">${num(n, unit)}</div>
      <div class="an-card-tip">${tip}</div>
    </div>`;
  return `
  <div class="an-cards">
    ${card('活跃用户数', c.activeUsers, '人', '有使用或业务操作的去重人数')}
    ${card('访问次数', c.visits, '次', '同一人间隔 30 分钟以上再用算新一次')}
    ${card('业务操作人数', c.operatorUsers, '人', '做过新增、修改、提交、提问等操作的去重人数')}
  </div>`;
}

/* ===================== ③ 活跃趋势 ===================== */

const bucketLabel = (b: AnalyticsBucket, grain: string): string =>
  grain === 'day' ? md(b.from) : `${md(b.from)}～${md(b.to)}`;

/** 纵轴上限：取整到方便读的刻度，至少 4 */
function niceMax(n: number): number {
  if (n <= 4) return 4;
  const step = Math.ceil(n / 4);
  return step * 4;
}

export function trendSvg(v: AnalyticsOverview): string {
  const pts = v.trend;
  if (!pts.length) return '';
  const W = 760;
  const H = 230;
  const L = 40;
  const R = 16;
  const T = 18;
  const B = 36;
  const n = pts.length;
  const band = (W - L - R) / n;
  const x = (i: number): number => L + band * (i + 0.5);
  const top = niceMax(Math.max(0, ...pts.map((p) => p.activeUsers ?? 0)));
  const y = (val: number): number => T + (H - T - B) * (1 - val / top);

  const grid = [0, 1, 2, 3, 4]
    .map((k) => {
      const val = (top / 4) * k;
      return `<line class="an-grid" x1="${L}" x2="${W - R}" y1="${y(val)}" y2="${y(val)}"/>
        <text class="an-ytick" x="${L - 8}" y="${y(val) + 4}">${val}</text>`;
    })
    .join('');

  /* 横轴标签太密就隔几个标一个（日粒度一个月 30 个桶时） */
  const every = Math.max(1, Math.ceil(n / (v.grain === 'day' ? 16 : 8)));
  const xlab = pts
    .map((p, i) =>
      i % every === 0 || i === n - 1
        ? `<text class="an-xtick${p.future ? ' fut' : ''}" x="${x(i)}" y="${H - B + 18}">${esc(bucketLabel(p, v.grain))}${p.partial && !p.future ? '*' : ''}</text>`
        : '',
    )
    .join('');

  /* ⚠ 未来的桶不画点也不连线：没发生的日子不是 0 人（`14` §3.6） */
  const series = (key: 'activeUsers' | 'operatorUsers'): string => {
    const segs: string[] = [];
    let cur: string[] = [];
    pts.forEach((p, i) => {
      const val = p[key];
      if (val === null) {
        if (cur.length) segs.push(cur.join(' '));
        cur = [];
        return;
      }
      cur.push(`${x(i).toFixed(1)},${y(val).toFixed(1)}`);
    });
    if (cur.length) segs.push(cur.join(' '));
    return segs.map((s) => `<polyline class="an-line ${key === 'activeUsers' ? 'a' : 'o'}" points="${s}"/>`).join('');
  };

  const showVal = n <= 16;
  const marks = pts
    .map((p, i) => {
      if (p.activeUsers === null || p.operatorUsers === null) return '';
      const tip = `${bucketLabel(p, v.grain)}${p.partial ? '（不完整）' : ''}：活跃 ${p.activeUsers} 人，业务操作 ${p.operatorUsers} 人`;
      return `<g><title>${esc(tip)}</title>
        <circle class="an-dot a" cx="${x(i)}" cy="${y(p.activeUsers)}" r="3.6"/>
        <rect class="an-dot o" x="${x(i) - 3}" y="${y(p.operatorUsers) - 3}" width="6" height="6"/>
        ${showVal ? `<text class="an-val" x="${x(i)}" y="${y(p.activeUsers) - 9}">${p.activeUsers}</text>` : ''}
      </g>`;
    })
    .join('');

  return `<svg class="an-svg" viewBox="0 0 ${W} ${H}" role="img" aria-label="活跃用户数与业务操作人数趋势">
    ${grid}${series('activeUsers')}${series('operatorUsers')}${marks}${xlab}
  </svg>`;
}

function trendHtml(v: AnalyticsOverview): string {
  const long = v.grain === 'day' && v.trend.length > 62;
  const partial = v.trend.some((b) => b.partial && !b.future);
  return `
  <section class="panel an-panel">
    <div class="panel-top">
      <h2>活跃趋势</h2>
      <span class="an-legend">
        <span><svg width="26" height="10" aria-hidden="true"><line class="an-line a" x1="1" x2="25" y1="5" y2="5"/><circle class="an-dot a" cx="13" cy="5" r="3"/></svg>活跃用户数（实线 · 圆点）</span>
        <span><svg width="26" height="10" aria-hidden="true"><line class="an-line o" x1="1" x2="25" y1="5" y2="5"/><rect class="an-dot o" x="10" y="2" width="6" height="6"/></svg>业务操作人数（虚线 · 方点）</span>
      </span>
      <span class="ck-sel an-grain">粒度
        <select id="an-grain" aria-label="趋势粒度">${GRAINS.map(([k, l]) => opt(k, state.an.grain, l)).join('')}</select></span>
    </div>
    <div class="an-chart">${trendSvg(v)}</div>
    <p class="an-foot">每个时间段单独去重，各段人数不能相加。${partial ? '带 * 的时间段不完整（被统计起点、所选范围或当前时刻截断）。' : ''}${
      long ? '范围较长，建议改用「周」或「双周」粒度。' : ''
    }</p>
  </section>`;
}

/* ===================== ④ 功能使用 ===================== */

function featureRow(f: AnalyticsFeatureStat): string {
  const open = state.an.openFeat === f.feature;
  const canOpen = f.actions.length > 0;
  const users =
    f.feature === 'assistant'
      ? `<span title="以已发送的提问为准，打开面板不计">${num(f.users, '人')}</span>`
      : num(f.users, '人');
  const note =
    f.feature === 'bulletins'
      ? '<span class="an-fnote">生成、编辑、删除自活跃统计上线起记录</span>'
      : '';
  const main = `
    <tr class="${canOpen ? 'can' : ''}${open ? ' open' : ''}" ${canOpen ? `data-anfeat="${f.feature}"` : ''}>
      <td><span class="an-fname">${canOpen ? `<span class="an-caret">${IC.chev}</span>` : '<span class="an-caret"></span>'}${esc(f.label)}</span>${note}</td>
      <td>${users}</td>
      <td>${f.opUsers === null ? dash('不适用：运营总览没有写操作') : num(f.opUsers, '人')}</td>
      <td>${f.opCount === null ? dash('不适用：运营总览没有写操作') : num(f.opCount, '次')}</td>
    </tr>`;
  if (!open) return main;
  return (
    main +
    `<tr class="an-sub"><td colspan="4"><ul class="an-acts">${f.actions
      .map(
        (a) =>
          `<li class="${a.count ? '' : 'zero'}"><span>${esc(a.label)}</span>${num(a.users, '人')}<i>／</i>${num(a.count, '次')}</li>`,
      )
      .join('')}</ul></td></tr>`
  );
}

function featuresHtml(v: AnalyticsOverview): string {
  return `
  <section class="panel an-panel">
    <div class="panel-top">
      <h2>功能使用</h2>
      <span class="sub">同一人会出现在多个功能里，各功能人数不能相加；点开行看具体操作</span>
    </div>
    <div class="an-scroll">
      <table class="matrix an-ft">
        <colgroup><col class="af-name"><col class="af-n"><col class="af-n"><col class="af-n"></colgroup>
        <thead><tr><th>功能</th><th>使用人数</th><th>操作人数</th><th>操作次数</th></tr></thead>
        <tbody id="an-feats">${v.features.map(featureRow).join('')}</tbody>
      </table>
    </div>
  </section>`;
}

/* ===================== ⑤ 用户明细 ===================== */

type SortKey = 'lastSeen' | 'activeDays' | 'visits' | 'ops';

/** 显示层筛选与排序。⚠ 不产生新数字；并列回落服务端默认序 */
export function visibleUsers(v: AnalyticsOverview): AnalyticsUserRow[] {
  const a = state.an;
  const q = a.q.trim();
  const order = new Map(v.users.map((u, i) => [u.personId, i]));
  const rows = v.users.filter(
    (u) => (!q || u.name.includes(q)) && (a.show === '' || (a.show === 'on' ? u.activeInRange : !u.activeInRange)),
  );
  const key = a.sort as SortKey | '';
  if (!key || key === 'lastSeen') {
    if (a.dir !== 'asc' || !key) return rows;
    /* 升序只把有记录的人倒过来，统计起点以来没有记录的人始终排末尾（`14` §5.2） */
    return [...rows.filter((u) => u.lastSeen !== null).reverse(), ...rows.filter((u) => u.lastSeen === null)];
  }
  const sign = a.dir === 'asc' ? 1 : -1;
  return [...rows].sort((x, y) => sign * (x[key] - y[key]) || (order.get(x.personId) ?? 0) - (order.get(y.personId) ?? 0));
}

function th(key: SortKey, label: string, tip: string): string {
  const cur = state.an.sort || 'lastSeen';
  const on = cur === key;
  const dir = state.an.dir;
  return `<th class="${on ? `on ${dir}` : ''}" ${on ? `aria-sort="${dir === 'asc' ? 'ascending' : 'descending'}"` : ''}>
    <button class="sortbtn" data-ansort="${key}" title="${esc(tip)}">${esc(label)}${IC.up}</button></th>`;
}

function userDetail(u: AnalyticsUserRow): string {
  if (!u.features.length) return '<p class="an-none">所选时间内没有记录。</p>';
  return `<ul class="an-ufeat">${u.features
    .map((f) => {
      const ops =
        f.opCount === null
          ? ''
          : f.opCount
            ? ` · 操作 ${num(f.opCount, '次')}<span class="an-uact">（${f.actions.map((a) => `${esc(a.label)} ${a.count}`).join('、')}）</span>`
            : ' · 未操作';
      return `<li><b>${esc(f.label)}</b>${num(f.days, '天')}有记录${ops}</li>`;
    })
    .join('')}</ul>`;
}

function userRow(u: AnalyticsUserRow): string {
  const open = state.an.openUsers.includes(u.personId);
  const tag = [
    /* 正式成员是主体，不标；其余类型（志愿者 / 导师 / 管理员 / 访客）都标出来（2026-10-22） */
    !u.type || u.type === 'member' ? '' : `<span class="mini">${TYPE_LABEL[u.type]}</span>`,
    u.enabled ? '' : '<span class="badge off">已停用</span>',
  ].join('');
  const last = u.lastSeen
    ? `<span class="num" title="统计起点（${SINCE_TEXT}）以来最后一次使用">${esc(hm(u.lastSeen))}</span>`
    : dash('统计起点以来没有记录');
  const main = `
    <tr class="can${open ? ' open' : ''}${u.activeInRange ? '' : ' idle'}" data-anuser="${esc(u.personId)}">
      <td><span class="an-uname"><span class="an-caret">${IC.chev}</span>${esc(u.name)}</span>${tag}</td>
      <td>${last}</td>
      <td>${num(u.activeDays, '天')}</td>
      <td>${num(u.visits, '次')}</td>
      <td>${num(u.ops, '次')}</td>
    </tr>`;
  return open ? main + `<tr class="an-sub"><td colspan="5">${userDetail(u)}</td></tr>` : main;
}

export function usersTable(v: AnalyticsOverview): string {
  const rows = visibleUsers(v);
  const count = `<span class="cnt" id="an-ucnt">显示 <span class="num">${rows.length}</span> / <span class="num">${v.users.length}</span> 人</span>`;
  if (!rows.length) {
    return (
      count +
      emptyHtml(EIC.search, '没有匹配的用户', '试试换个姓名，或把筛选放宽', '<button class="tbtn" data-an="uclear">清空筛选</button>', 'sm')
    );
  }
  return `${count}
    <div class="an-scroll">
      <table class="matrix an-ut">
        <colgroup><col class="au-name"><col class="au-last"><col class="au-n"><col class="au-n"><col class="au-n"></colgroup>
        <thead><tr>
          <th>姓名</th>
          ${th('lastSeen', '最近使用时间', '统计起点以来最后一次使用，可早于所选时间')}
          ${th('activeDays', '活跃天数', '所选时间内有记录的天数')}
          ${th('visits', '访问次数', '所选时间内的访问次数')}
          ${th('ops', '操作次数', '所选时间内的业务操作次数')}
        </tr></thead>
        <tbody>${rows.map(userRow).join('')}</tbody>
      </table>
    </div>`;
}

function usersHtml(v: AnalyticsOverview): string {
  const a = state.an;
  return `
  <section class="panel an-panel">
    <div class="panel-top">
      <h2>用户使用明细</h2>
      <span class="sub">搜索与筛选只作用于本表，不改变上方数字</span>
      <div class="an-uctrl">
        <div class="search">
          <span class="si" aria-hidden="true">${IC.search}</span>
          <input type="text" id="an-q" value="${esc(a.q)}" placeholder="搜索姓名" aria-label="搜索姓名">
        </div>
        <span class="ck-sel">
          <select id="an-show" aria-label="按本期是否活跃筛选">
            ${opt('', a.show, '全部用户')}${opt('on', a.show, '本期活跃')}${opt('off', a.show, '本期未活跃')}
          </select></span>
      </div>
    </div>
    <div id="an-users">${usersTable(v)}</div>
  </section>`;
}

/* ===================== 统计说明 ===================== */

const notesHtml = (v: AnalyticsOverview): string => `
  <details class="an-notes">
    <summary>统计说明</summary>
    <ul>
      <li>统计始于 <b class="num">${SINCE_TEXT}</b>（北京时间），此前的记录不纳入；本次数据截至 <b class="num">${esc(hm(v.range.asOf))}</b>。</li>
      <li>范围为全部真实账号，管理员与学员一起计算、不单列；测试账号 <b>test</b> 不计入。</li>
      <li><b>活跃</b>：打开或操作过系统中的页面，或做过业务操作。登录、图片加载等自动发生的请求不算。</li>
      <li><b>访问次数</b>：同一人相邻两次使用间隔不到 30 分钟算同一次，满 30 分钟算新一次；换设备或重新登录不另算。停留在一个页面超过 30 分钟不操作，之后再用会算成两次。</li>
      <li><b>业务操作</b>：新增、修改、删除、提交、提问等由服务器确认成功的操作；只看不改不算。代录的操作算到实际操作的人。</li>
      <li>「本期未活跃」只说明这段时间没有用系统，不等于没有参加训练营活动。</li>
      <li>依据现存访问与操作记录计算，个别记录可能因服务异常未写入。</li>
    </ul>
  </details>`;

/* ===================== 装配 ===================== */

export function panel(v: AnalyticsOverview): string {
  const r = v.range;
  if (r.outOfScope) {
    return emptyHtml(EIC.cal, `不在统计范围内（自 ${SINCE_TEXT} 起）`, '所选时间早于统计起点，请选择 2026-09-14 及之后的日期') + notesHtml(v);
  }
  if (!r.start) {
    return emptyHtml(EIC.cal, '所选时间还没开始', '请选择今天或更早的日期') + notesHtml(v);
  }
  return `
    ${rangeNote(v)}
    ${cardsHtml(v)}
    ${trendHtml(v)}
    ${featuresHtml(v)}
    ${usersHtml(v)}
    ${notesHtml(v)}`;
}

/* ===================== 事件 ===================== */

const rerenderUsers = (v: AnalyticsOverview): void => {
  const box = $('#an-users');
  if (box) box.innerHTML = usersTable(v);
};

export function onEvent(e: Event, v: AnalyticsOverview): void {
  const t = e.target;
  if (!(t instanceof Element)) return;
  const a = state.an;

  if (e.type === 'change' && t instanceof HTMLSelectElement) {
    if (t.id === 'an-preset') {
      a.preset = t.value;
      a.openFeat = null;
      a.openUsers = [];
      /* 自定义要等选好起止日点「查询」；其余预设选了就查 */
      if (a.preset === 'custom') {
        a.from = a.from || v.range.from;
        a.to = a.to || v.range.to;
      }
      refreshCurrent();
      return;
    }
    if (t.id === 'an-grain') {
      a.grain = t.value;
      refreshCurrent();
      return;
    }
    if (t.id === 'an-show') {
      a.show = t.value as typeof a.show;
      rerenderUsers(v);
      return;
    }
  }

  if (e.type === 'input' && t instanceof HTMLInputElement && t.id === 'an-q') {
    a.q = t.value;
    rerenderUsers(v);
    return;
  }

  if (e.type !== 'click') return;

  if (t.closest('[data-an="query"]')) {
    const from = $<HTMLInputElement>('#an-from')?.value ?? '';
    const to = $<HTMLInputElement>('#an-to')?.value ?? '';
    if (!from || !to) return toast('请选择开始和结束日期');
    /* ⚠ 倒置、超长等由服务端校验并报错，这里只挡「没填」 */
    a.from = from;
    a.to = to;
    a.openUsers = [];
    refreshCurrent();
    return;
  }

  if (t.closest('[data-an="uclear"]')) {
    a.q = '';
    a.show = '';
    const q = $<HTMLInputElement>('#an-q');
    if (q) q.value = '';
    const sel = $<HTMLSelectElement>('#an-show');
    if (sel) {
      sel.value = '';
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    }
    rerenderUsers(v);
    return;
  }

  const sort = t.closest<HTMLElement>('[data-ansort]');
  if (sort) {
    const key = sort.dataset.ansort as SortKey;
    const cur = a.sort || 'lastSeen';
    a.dir = cur === key ? (a.dir === 'desc' ? 'asc' : 'desc') : 'desc';
    a.sort = key;
    rerenderUsers(v);
    return;
  }

  const feat = t.closest<HTMLElement>('[data-anfeat]');
  if (feat) {
    a.openFeat = a.openFeat === feat.dataset.anfeat ? null : (feat.dataset.anfeat ?? null);
    const body = $('#an-feats');
    if (body) body.innerHTML = v.features.map(featureRow).join('');
    return;
  }

  const user = t.closest<HTMLElement>('[data-anuser]');
  if (user) {
    const id = user.dataset.anuser ?? '';
    a.openUsers = a.openUsers.includes(id) ? a.openUsers.filter((x) => x !== id) : [...a.openUsers, id];
    rerenderUsers(v);
  }
}
